/**
 * Unified museum collection search across Nordic APIs.
 * Given a coordinate + country, queries the appropriate museum API
 * to find nearby archaeological objects in museum collections.
 *
 * Sources:
 *   DK → Nationalmuseet (api.natmus.dk) — Elasticsearch POST
 *   NO → DigitaltMuseum (api.dimu.org) — Solr geofilt
 *   SE → K-samsök (kulturarvsdata.se) — XML bounding box
 *   FI → Finna.fi (api.finna.fi) — Solr geofilt
 *   GB/IE/IM → V&A + Portable Antiquities Scheme
 *
 * Falls back to Wikidata P195 for all countries. Read lazily by useEnrichment
 * when a pin is opened, and every branch has a hard timeout because the whole
 * enrichment pipeline is raced against an 8-second deadline.
 */

import type { Country, EnrichmentData } from "../types/index.ts";
import { sanitizeImageUrl } from "../utils/images.ts";
import { lookupMuseumUrl } from "./enrichment.ts";

export type MuseumFind = NonNullable<EnrichmentData["museumFinds"]>[number];

// ─── Denmark: Nationalmuseet (api.natmus.dk) ───

async function searchNatmus(lat: number, lng: number, siteName: string): Promise<MuseumFind[]> {
  // Search for images (cumulus_public_assets) by site name in DO (Danmarks Oldtid) + DMR collections
  const query = {
    query: { bool: { must: [
      { query_string: { query: siteName.replace(/[+\-=&|><!(){}[\]^"~*?:\\/]/g, " "), default_operator: "AND" } },
      { term: { _index: "cumulus_public_assets" } },
      { terms: { "collection.keyword": ["DO", "DMR", "FHM", "FLM", "ES"] } },
    ] } },
    size: 5,
    _source: ["text.da-DK", "collection", "id", "tags.verified", "rights.license", "physicalReference"],
  };

  try {
    const res = await fetch("https://api.natmus.dk/search/public/raw", {
      method: "POST",
      headers: { "Content-Type": "application/json", "User-Agent": "FornlandApp/1.0" },
      body: JSON.stringify(query),
      signal: AbortSignal.timeout(6000),
    });
    if (!res.ok) return [];
    const data = await res.json();
    const hits = data.hits?.hits ?? [];
    const results: MuseumFind[] = [];
    for (const hit of hits) {
      const src = hit._source;
      const title = src?.text?.["da-DK"]?.title;
      if (!title) continue;
      const coll = src?.collection ?? "DO";
      const id = src?.id;
      if (!id) continue;
      // Only use CC-licensed images
      const license = src?.rights?.license ?? "";
      if (!license.includes("CC") && !license.includes("Public Domain")) continue;
      const desc = src?.text?.["da-DK"]?.description?.slice(0, 200);
      results.push({
        name: title,
        description: desc,
        museum: "Nationalmuseet",
        image: `https://samlinger.natmus.dk/${coll}/asset/${id}.jpg?maxsize=300`,
        wikipediaUrl: undefined,
        wikidataUrl: `https://samlinger.natmus.dk/${coll.toUpperCase()}/asset/${id}`,
      });
    }
    return results;
  } catch { return []; }
}

// ─── Norway: DigitaltMuseum (api.dimu.org) ───

/** Great-circle kilometres. Only used to order ten rows, so the sphere is fine. */
function distanceKm(aLat: number, aLng: number, bLat: number, bLng: number): number {
  const rad = Math.PI / 180;
  const dLat = (bLat - aLat) * rad;
  const dLng = (bLng - aLng) * rad;
  const h = Math.sin(dLat / 2) ** 2
    + Math.cos(aLat * rad) * Math.cos(bLat * rad) * Math.sin(dLng / 2) ** 2;
  return 6371 * 2 * Math.asin(Math.sqrt(h));
}

/**
 * How far out a find still counts as being at this place, in kilometres.
 *
 * These two numbers carry more weight than they look like they do. See the
 * note in searchDigitaltMuseum about `rows` being pinned at ten: the radius is
 * the whole relevance claim, because what comes back is index order inside it
 * and not the ten nearest. Around Oslo 1 km holds 481 archaeological objects
 * and 5 km holds 11.977, so the wide circle alone would answer "somewhere in
 * town". But the country is not Oslo. Sampling real Askeladden localities on
 * 22 Aug 2026, 1 km found something for 84% of pins in Oslo, 52% in Telemark,
 * 36% in Lofoten and 12% in Trøndelag.
 *
 * So the tight circle is asked first and the wide one only when it came back
 * empty, which is exactly where the wide one is small too: Trøndelag answers 3
 * objects at 5 km, Lofoten 1. Both calls run in well under a second.
 */
const DIMU_RADIUS_KM = [1, 5];

interface DimuDoc {
  "artifact.ingress.title"?: string;
  "artifact.uniqueId"?: string;
  "artifact.defaultMediaIdentifier"?: string;
  "artifact.ingress.description"?: string;
  "artifact.ingress.subjects"?: string[];
  "artifact.coordinate"?: string;
}

/** Solr answers 200 with the real code buried in `responseHeader`. */
function dimuDocs(data: unknown): DimuDoc[] {
  const body = data as { responseHeader?: { status?: number }; response?: { docs?: DimuDoc[] } };
  if (body?.responseHeader?.status !== 0) return [];
  return body.response?.docs ?? [];
}

/**
 * Museum objects registered near a point, from Norwegian and Swedish
 * collections.
 *
 * Four things about this endpoint decide the query, and every one of them was
 * getting the answer wrong before. Without `api.key` it answers 302 to its own
 * HTML documentation, so `res.json()` throws into a swallowing catch. A
 * function sort is refused by the gateway ("sort param could not be parsed as
 * a query"), and the refusal arrives as HTTP 200 carrying `status: 400` inside
 * `responseHeader`, which is why `res.ok` proves nothing here. `rows` is
 * ignored and pinned at 10 whatever is asked for. And the ten are index order,
 * not the ten nearest, so DIMU_RADIUS_KM is doing the work a distance sort
 * would have done and the sort below only orders what arrived.
 *
 * Called from fetchMuseumCollections for Norwegian pins.
 */
async function searchDigitaltMuseum(lat: number, lng: number): Promise<MuseumFind[]> {
  const ask = async (km: number): Promise<DimuDoc[]> => {
    const geofilt = encodeURIComponent(
      `{!geofilt sfield=artifact.coordinate pt=${lat},${lng} d=${km}}`,
    );
    const url = `https://api.dimu.org/api/solr/select?q=*:*`
      + `&fq=${geofilt}`
      + `&fq=artifact.type:Thing`
      + `&fq=artifact.subtype:Arkeologi`
      + `&fq=artifact.hasPictures:true`
      + `&wt=json&api.key=demo`;
    const res = await fetch(url, {
      headers: { "User-Agent": "FornlandApp/1.0" },
      signal: AbortSignal.timeout(6000),
    });
    if (!res.ok) return [];
    return dimuDocs(await res.json());
  };

  try {
    let docs: DimuDoc[] = [];
    for (const km of DIMU_RADIUS_KM) {
      docs = await ask(km);
      if (docs.length) break;
    }
    return docs
      .map((doc) => {
        const [dLat, dLng] = String(doc["artifact.coordinate"] ?? "").split(",").map(Number);
        return { doc, km: Number.isFinite(dLat) && Number.isFinite(dLng) ? distanceKm(lat, lng, dLat, dLng) : Infinity };
      })
      .sort((a, b) => a.km - b.km)
      .slice(0, 5)
      .map(({ doc }) => {
        const subjects = Array.isArray(doc["artifact.ingress.subjects"])
          ? doc["artifact.ingress.subjects"].join(", ")
          : undefined;
        const mediaId = doc["artifact.defaultMediaIdentifier"];
        const uniqueId = doc["artifact.uniqueId"];
        return {
          name: doc["artifact.ingress.title"] || "Arkæologisk fund",
          description: doc["artifact.ingress.description"]?.slice(0, 200) || subjects,
          museum: "DigitaltMuseum",
          image: mediaId ? `https://dms-cf-01.dimu.org/image/${mediaId}?dimension=300x300` : undefined,
          wikipediaUrl: undefined,
          wikidataUrl: uniqueId ? `https://digitaltmuseum.org/${uniqueId}` : "",
        } as MuseumFind;
      })
      .filter((f) => f.name && f.wikidataUrl);
  } catch { return []; }
}

// ─── Sweden: K-samsök (kulturarvsdata.se) ───

async function searchKsamsok(lat: number, lng: number): Promise<MuseumFind[]> {
  const d = 0.05; // ~5km at Nordic latitudes
  const bbox = `"${(lng - d).toFixed(4)} ${(lat - d).toFixed(4)} ${(lng + d).toFixed(4)} ${(lat + d).toFixed(4)}"`;
  const url = `https://kulturarvsdata.se/ksamsok/api?method=search&version=1.1&hitsPerPage=10`
    + `&query=boundingBox=/WGS84 ${encodeURIComponent(bbox)}`
    + `&apiKey=test`;

  try {
    const res = await fetch(url, {
      headers: { "User-Agent": "FornlandApp/1.0", Accept: "application/xml" },
      signal: AbortSignal.timeout(8000),
    });
    if (!res.ok) return [];
    const xml = await res.text();

    // Parse XML response — extract records from <record> or <Entity> blocks
    const results: MuseumFind[] = [];
    // Use regex-based extraction for reliability (no DOMParser in all environments)
    const records = xml.split(/<pres:item /);
    for (const rec of records.slice(1, 6)) { // max 5
      const label = rec.match(/<pres:itemLabel>([^<]+)/)?.[1];
      const thumb = rec.match(/<pres:image[^>]*>([^<]+)/)?.[1];
      const pageUrl = rec.match(/<pres:entityUri>([^<]+)/)?.[1];
      if (!label) continue;
      // Skip overly generic labels
      if (label.length < 3) continue;
      results.push({
        name: label,
        description: undefined,
        museum: "K-samsök",
        image: thumb ? sanitizeImageUrl(thumb) || undefined : undefined,
        wikipediaUrl: undefined,
        wikidataUrl: pageUrl ? pageUrl.replace("/rdf/", "/html/") : "",
      });
    }
    return results;
  } catch { return []; }
}

// ─── Finland: Finna.fi ───

async function searchFinna(lat: number, lng: number): Promise<MuseumFind[]> {
  const params = new URLSearchParams({
    lookfor: "",
    "filter[]": `{!geofilt sfield=location_geo pt=${lat},${lng} d=10}`,
    limit: "5",
  });
  // Add multiple filter[] and field[] params
  const url = `https://api.finna.fi/v1/search?${params.toString()}`
    + `&filter[]=building:0/Museovirasto/`
    + `&filter[]=format:0/PhysicalObject/`
    + `&field[]=id&field[]=title&field[]=images&field[]=summary&field[]=buildings&field[]=geoCenter`;

  try {
    const res = await fetch(url, {
      headers: { "User-Agent": "FornlandApp/1.0" },
      signal: AbortSignal.timeout(6000),
    });
    if (!res.ok) return [];
    const data = await res.json();
    const records = data.records ?? [];
    interface FinnaRec {
      title?: string;
      id?: string;
      summary?: string[];
      images?: unknown[];
      buildings?: Array<{ translated?: string }>;
    }
    return (records as FinnaRec[]).map((rec) => {
      const title = rec.title;
      const id = rec.id ?? "";
      const summary = Array.isArray(rec.summary) ? rec.summary[0]?.slice(0, 200) : undefined;
      const img = Array.isArray(rec.images) && rec.images[0]
        ? `https://api.finna.fi/Cover/Show?source=Solr&id=${encodeURIComponent(id)}&index=0&size=large`
        : undefined;
      const building = Array.isArray(rec.buildings) ? rec.buildings[0]?.translated : undefined;
      return {
        name: title || "Arkeologisk löytö",
        description: summary,
        museum: building || "Museovirasto",
        image: img,
        wikipediaUrl: undefined,
        wikidataUrl: `https://finna.fi/Record/${encodeURIComponent(id)}`,
      } as MuseumFind;
    }).filter((f) => f.name);
  } catch { return []; }
}

// ─── UK: Victoria and Albert Museum (api.vam.ac.uk) ───

async function searchVandA(siteName: string, _siteType?: string): Promise<MuseumFind[]> {
  try {
    // Build a search query from site name — use first part before comma/parenthesis
    const q = encodeURIComponent(siteName.split(/[,(]/)[0].trim());
    const url = `https://api.vam.ac.uk/v2/objects/search?q=${q}&images_exist=1&limit=3&order_by=relevance`;
    const res = await fetch(url, {
      headers: { "User-Agent": "FornlandApp/1.0" },
      signal: AbortSignal.timeout(5000),
    });
    if (!res.ok) return [];
    const data = await res.json();
    const records = data.records ?? [];
    interface VamRec { _primaryTitle?: string; _primaryImageId?: string; _primaryDate?: string; objectType?: string; systemNumber?: string }
    return (records as VamRec[])
      .filter((r): r is VamRec => !!r._primaryTitle && !!r._primaryImageId)
      .slice(0, 3)
      .map((r) => ({
        name: r._primaryTitle!,
        description: r._primaryDate ? `${r.objectType || ''} \u00b7 ${r._primaryDate}`.replace(/^\s*\u00b7\s*/, '') : r.objectType,
        museum: "Victoria and Albert Museum",
        image: `https://framemark.vam.ac.uk/collections/${r._primaryImageId}/full/!300,300/0/default.jpg`,
        wikipediaUrl: undefined,
        wikidataUrl: `https://collections.vam.ac.uk/item/${r.systemNumber}/`,
      }));
  } catch { return []; }
}

// ─── Wikidata P195 fallback (all countries) ───

async function searchWikidataP195(lat: number, lng: number, userLang?: string): Promise<MuseumFind[]> {
  const langPrio = userLang ? [userLang, "en", "da", "no", "sv"].filter((v, i, a) => a.indexOf(v) === i).join(",") : "da,en,no,sv";
  const query = `SELECT ?item ?itemLabel ?itemDescription ?image ?collectionLabel WHERE {
    ?item wdt:P195 ?collection .
    ?item wdt:P625 ?coord .
    FILTER(geof:distance(?coord, "Point(${lng} ${lat})"^^geo:wktLiteral) < 5)
    OPTIONAL { ?item wdt:P18 ?image }
    SERVICE wikibase:label { bd:serviceParam wikibase:language "${langPrio}" }
  } LIMIT 5`;
  try {
    const res = await fetch(
      `https://query.wikidata.org/sparql?format=json&query=${encodeURIComponent(query)}`,
      { headers: { Accept: "application/sparql-results+json", "User-Agent": "FornlandApp/1.0" }, signal: AbortSignal.timeout(8000) },
    );
    if (!res.ok) return [];
    const text = await res.text();
    if (!text.startsWith("{")) return [];
    const data = JSON.parse(text);
    const rows = data.results?.bindings ?? [];
    const seen = new Set<string>();
    const results: MuseumFind[] = [];
    for (const r of rows) {
      const qid = r.item?.value?.split("/").pop() ?? "";
      if (seen.has(qid)) continue;
      seen.add(qid);
      const name = r.itemLabel?.value;
      const museum = r.collectionLabel?.value;
      if (!name || !museum || /^Q\d+$/.test(name)) continue;
      const img = r.image?.value ? (sanitizeImageUrl(r.image.value) ?? "") + "?width=300" : undefined;
      results.push({
        name,
        description: r.itemDescription?.value?.slice(0, 200),
        museum,
        image: img || undefined,
        wikipediaUrl: undefined,
        wikidataUrl: `https://www.wikidata.org/wiki/${qid}`,
      });
    }
    return results;
  } catch { return []; }
}

// ─── England/Wales: Portable Antiquities Scheme (finds.org.uk) ───

async function searchPAS(lat: number, lng: number): Promise<MuseumFind[]> {
  const d = 0.04; // ~4.5 km
  const bbox = `${(lng - d).toFixed(4)},${(lat - d).toFixed(4)},${(lng + d).toFixed(4)},${(lat + d).toFixed(4)}`;
  const url = `https://finds.org.uk/database/search/results/bbox/${bbox}/thumbnail/1/format/json`;
  try {
    const res = await fetch(url, { signal: AbortSignal.timeout(8000) });
    if (!res.ok) return [];
    const data = await res.json();
    interface PasRec { id?: string | number; objecttype?: string; broadperiod?: string; description?: string; institution?: string; filename?: string; imagedir?: string }
    return ((data.results ?? []) as PasRec[]).slice(0, 6).map((r) => ({
      name: `${titleCase(r.objecttype || "Find")} (${titleCase(r.broadperiod || "")})`.replace(/ \(\)/, ""),
      description: r.description?.slice(0, 250) || undefined,
      museum: r.institution ? `PAS — ${r.institution}` : "Portable Antiquities Scheme",
      image: r.filename && r.imagedir ? `https://finds.org.uk/${r.imagedir}${r.filename}` : undefined,
      wikidataUrl: `https://finds.org.uk/database/artefacts/record/id/${r.id}`,
    }));
  } catch { return []; }
}

function titleCase(s: string): string {
  return s.toLowerCase().replace(/\b\w/g, c => c.toUpperCase()).replace(/\s+/g, " ").trim();
}

// ─── Unified search: pick API by country, merge with Wikidata ───

export async function fetchMuseumCollections(
  lat: number, lng: number, country: Country, siteName: string, userLang?: string,
  responsibleMuseum?: string,
): Promise<MuseumFind[] | undefined> {
  // Run country-specific API + Wikidata in parallel
  const countrySearch =
    country === "DK" ? searchNatmus(lat, lng, siteName)
    : country === "NO" ? searchDigitaltMuseum(lat, lng)
    : country === "SE" ? searchKsamsok(lat, lng)
    : country === "FI" ? searchFinna(lat, lng)
    // No DigitaltMuseum here. It is a Norwegian and Swedish archive, and the
    // archaeology filter finds nothing at all within 5 km of London, so the
    // call only ever spent a request and up to six seconds of the enrichment
    // deadline on an empty answer.
    : country === "GB" || country === "IE" || country === "IM"
      ? Promise.all([searchVandA(siteName).catch(() => [] as MuseumFind[]), searchPAS(lat, lng).catch(() => [] as MuseumFind[])])
          .then(([va, pas]) => [...va, ...pas])
    : Promise.resolve([] as MuseumFind[]);

  const [apiResults, wdResults] = await Promise.allSettled([
    countrySearch,
    searchWikidataP195(lat, lng, userLang),
  ]);

  const api = apiResults.status === "fulfilled" ? apiResults.value : [];
  const wd = wdResults.status === "fulfilled" ? wdResults.value : [];

  // Merge: API results first, then Wikidata (deduplicate by name similarity)
  const all = [...api];
  const nameSet = new Set(api.map(f => f.name.toLowerCase()));
  for (const w of wd) {
    if (!nameSet.has(w.name.toLowerCase())) {
      all.push(w);
      nameSet.add(w.name.toLowerCase());
    }
  }

  // For Danish pins: add a "search local museum" link if we know the responsible museum
  if (country === "DK" && responsibleMuseum) {
    const museumUrl = lookupMuseumUrl(responsibleMuseum);
    if (museumUrl && museumUrl !== "https://natmus.dk") {
      // Construct search URL — most Danish museum websites use WordPress with ?s= search
      const searchTerm = siteName.split(/[,(]/)[0].trim(); // Use first part of name before comma/parenthesis
      const searchUrl = `${museumUrl}/?s=${encodeURIComponent(searchTerm)}`;
      // Only add if this museum isn't already represented
      if (!all.some(f => f.museum.toLowerCase().includes(responsibleMuseum.toLowerCase().split(" ")[1] ?? ""))) {
        all.push({
          name: `Søg på ${responsibleMuseum}`,
          description: `Se hvad ${responsibleMuseum} har om dette sted`,
          museum: responsibleMuseum,
          image: undefined,
          wikipediaUrl: undefined,
          wikidataUrl: searchUrl,
        });
      }
    }
  }

  return all.length > 0 ? all.slice(0, 8) : undefined;
}
